import { Timestamp } from "firebase/firestore";
import { GameRequestDoc } from "@/features/game/types";
import { WorldStyleId } from "@/features/game/world/styled/styles";
import {
  Appearance,
  BookBeat,
  BookCanon,
  BookCharacter,
  BookEnding,
  BookFact,
  BookFrame,
  BookItem,
  BookZone,
  DecisionPoint,
  EndingRule,
  WorldSpec,
} from "@/features/library/types";

/** Mirrors functions/python/domain/entity/book_project.py. */
export const PROJECT_STEPS = [
  "extract",
  "plot",
  "world",
  "looks",
  "illustrate",
  "check",
  "publish",
] as const;
export type ProjectStep = (typeof PROJECT_STEPS)[number];

export type StageStatus = "running" | "done" | "failed";

export interface StageState {
  status: StageStatus;
  request_id: string;
  updated_at: Timestamp;
  error?: string | null;
}

/** A BookCanon that fills up step by step: every section may still be missing. */
export interface ProjectDraft {
  frame?: BookFrame | null;
  zones?: BookZone[];
  characters?: BookCharacter[];
  items?: BookItem[];
  facts?: BookFact[];
  beats?: BookBeat[];
  decision_point?: DecisionPoint | null;
  endings?: BookEnding[];
  ending_rules?: EndingRule[];
  world?: WorldSpec | null;
}

export interface ValidationIssue {
  code: string;
  message: string;
  /** Where it points, like "beats.2.outcomes". */
  path: string;
}

export interface ValidationReport {
  ok: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  canon_ending: string | null;
  canon_beats: string[];
  reachable_endings: string[];
  search_complete: boolean;
  checked_at: Timestamp | string;
}

export interface CharacterLook {
  character: string;
  appearance: Appearance;
}

export interface ProjectDoc {
  user_id: string;
  title: string;
  author: string | null;
  text_path: string;
  text_chars: number;
  style: WorldStyleId | null;
  draft: ProjectDraft;
  stages?: Partial<Record<ProjectStep, StageState>>;
  /** Zone id to the Storage path of its concept art. */
  concept_art?: Record<string, string>;
  report?: ValidationReport | null;
  book_id?: string | null;
  created_at: Timestamp | null;
  updated_at: Timestamp | null;
}

export type Project = ProjectDoc & { id: string };

export type ProjectRequestDoc = GameRequestDoc<Record<string, unknown>> & {
  user_id: string;
  project_id: string;
  step: ProjectStep;
  created_at: Timestamp | null;
};

export type ProjectRequest = ProjectRequestDoc & { id: string };

/** Backend errors arrive as "ProjectNotReadyError: The world step needs…". */
export function stepErrorMessage(error: { message: string }): string {
  return error.message.replace(/^\w+Error: /, "");
}

/** The editor's steps, in order. */
export const EDITOR_STEPS = [
  "source",
  "bible",
  "style",
  "world",
  "cast",
  "story",
  "publish",
] as const;
export type EditorStepId = (typeof EDITOR_STEPS)[number];

export function isEditorStep(value: unknown): value is EditorStepId {
  return (EDITOR_STEPS as readonly unknown[]).includes(value);
}

/** The draft as a canon, when every section is there. */
export function draftCanon(draft: ProjectDraft): BookCanon | null {
  const {
    zones,
    characters,
    beats,
    decision_point: decisionPoint,
    endings,
    ending_rules: endingRules,
    world,
  } = draft;
  if (
    !zones?.length ||
    !characters?.length ||
    !beats?.length ||
    !decisionPoint ||
    !endings?.length ||
    !endingRules?.length ||
    !world
  ) {
    return null;
  }
  return {
    ...draft,
    zones,
    characters,
    beats,
    decision_point: decisionPoint,
    endings,
    ending_rules: endingRules,
    world,
    items: draft.items ?? [],
    frame: draft.frame ?? undefined,
    facts: draft.facts ?? [],
  };
}

export const MAX_TITLE_LENGTH = 200;
export const MIN_TEXT_CHARS = 1000;
export const MAX_TEXT_CHARS = 3_000_000;
